// components/CalisanDetay.js

import React, { useEffect, useState } from "react";
import { supabase } from "../supabaseClient";
import { format, eachDayOfInterval, addDays } from "date-fns";
import { tr as trLocale } from "date-fns/locale";
import Modal from "./Modal";
import HataBildirimi from "./HataBildirimi";

function CalisanDetay({ calisan, inDialog = false }) {
  const [girisCikis, setGirisCikis] = useState([]);
  const [baslangic, setBaslangic] = useState(format(new Date(), "yyyy-MM-dd"));
  const [bitis, setBitis] = useState(format(new Date(), "yyyy-MM-dd"));
  const [session, setSession] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [showLogin, setShowLogin] = useState(false);
  const [editing, setEditing] = useState(null); // { id?, giris_tarihi?, cikis_tarihi?, isNew? }
  const [editValues, setEditValues] = useState({ giris: "", cikis: "" });
  const [lockChecked, setLockChecked] = useState(false);
  const [showHataBildirimi, setShowHataBildirimi] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const dayStartHour = 5; // iş günü başlangıcı

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setSession(data.session));
    const { data: sub } = supabase.auth.onAuthStateChange((_e, sess) => setSession(sess));
    return () => sub.subscription.unsubscribe();
  }, []);

  // Kullanıcı profilini yükle
  useEffect(() => {
    async function loadUserProfile() {
      if (!session?.user) {
        setUserProfile(null);
        return;
      }
      
      try {
        const email = session?.user?.email || null;
        
        // Önce kullanici_profilleri tablosundan kontrol et
        let { data, error } = await supabase
          .from("kullanici_profilleri")
          .select("*")
          .eq("email", email)
          .maybeSingle();
        
        if (data) {
          setUserProfile(data);
        } else {
          // Profil bulunamadı - admin_users tablosundan kontrol et
          const { data: authUser } = await supabase.auth.getUser();
          
          if (authUser?.user?.id) {
            const adminCheck = await supabase
              .from("admin_users")
              .select("user_id")
              .eq("user_id", authUser.user.id)
              .maybeSingle();
            
            if (adminCheck.data) {
              // Admin kullanıcı - geçici admin profili oluştur
              const tempAdminProfile = {
                id: -1,
                kullanici_id: null,
                email: email,
                isim: "Admin",
                soyisim: "Kullanıcı",
                is_admin: true,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString()
              };
              setUserProfile(tempAdminProfile);
            } else {
              setUserProfile(null);
            }
          } else {
            setUserProfile(null);
          }
        }
      } catch (err) {
        console.error("Profil yükleme hatası:", err);
        setUserProfile(null);
      }
    }

    loadUserProfile();
  }, [session]);

  useEffect(() => {
    async function fetchGirisCikis() {
      if (!calisan) return;
      const endExclusive = format(addDays(new Date(bitis), 1), "yyyy-MM-dd");
      const { data, error } = await supabase
        .from("personel_giris_cikis_duzenli")
        .select("*")
        .eq("kullanici_id", calisan.kullanici_id ?? calisan.id)
        .gte("giris_tarihi", baslangic)
        .lt("giris_tarihi", endExclusive)
        .order("giris_tarihi", { ascending: true });

      if (error) {
        console.error("Hata:", error);
        setGirisCikis([]);
      } else {
        setGirisCikis(data || []);
      }
    }
    fetchGirisCikis();
  }, [calisan, baslangic, bitis]);

  const gunler = eachDayOfInterval({
    start: new Date(baslangic),
    end: new Date(bitis),
  });

  // Kayıtları iş günü kaydırmalı gösterime göre grupla
  const recordsForDisplay = girisCikis.map((k) => {
    const girisDt = new Date(k.giris_tarihi);
    const cikisDt = k.cikis_tarihi ? new Date(k.cikis_tarihi) : null;
    const sure = cikisDt ? (cikisDt - girisDt) / (1000 * 60 * 60) : 0;
    const displayDate = k.workday_date
      ? k.workday_date
      : format(new Date(girisDt.getTime() - dayStartHour * 60 * 60 * 1000), "yyyy-MM-dd");
    return { ...k, girisDt, cikisDt, displayDate, sure };
  });

  // Günlük gruplar
  const gunGruplari = gunler.map((gun) => {
    const dateStr = format(gun, "yyyy-MM-dd");
    const recs = recordsForDisplay
      .filter((r) => r.displayDate === dateStr)
      .sort((a, b) => a.girisDt - b.girisDt);

    const kayitlar = recs.length === 0
      ? [{ giris: "-", cikis: "Devamsız", sure: 0, _row: null, isAbsent: true }]
      : recs.map((r) => ({
          giris: format(r.girisDt, "HH:mm"),
          cikis: r.cikisDt ? format(r.cikisDt, "HH:mm") : "-",
          sure: r.sure,
          _row: r,
          isAbsent: false,
        }));

    const recordCount = recs.length;
    const hasIncompleteRecord = recs.some((r) => !r.cikisDt);
    const isToday = dateStr === format(new Date(), "yyyy-MM-dd");
    // Tek kayıt + çıkış eksik + düzeltilmemiş + bugün değil → dikkat çek
    const needsAttention =
      !isToday &&
      recordCount === 1 &&
      hasIncompleteRecord &&
      !recs.some((r) => r.admin_locked === true);

    return {
      dateStr,
      gunAdi: format(gun, "EEEE", { locale: trLocale }),
      gunKisa: format(gun, "d MMM yyyy", { locale: trLocale }),
      kayitlar,
      isAbsent: recordCount === 0,
      recordCount,
      hasEdited: recs.some((r) => r.admin_locked === true),
      needsAttention,
      gunToplam: kayitlar.reduce((s, k) => s + k.sure, 0),
    };
  });

  const gunlerWithData = gunGruplari.flatMap((grup) =>
    grup.kayitlar.map((k) => ({
      tarih: grup.dateStr,
      gun: grup.gunAdi,
      giris: k.giris,
      cikis: k.cikis,
      sure: k.sure,
      _row: k._row,
    }))
  );

  const toplamSure = gunlerWithData
    .reduce((sum, g) => sum + g.sure, 0)
    .toFixed(2);

  async function refreshGirisCikis() {
    const endExclusive = format(addDays(new Date(bitis), 1), "yyyy-MM-dd");
    const { data } = await supabase
      .from("personel_giris_cikis_duzenli")
      .select("*")
      .eq("kullanici_id", calisan.kullanici_id ?? calisan.id)
      .gte("giris_tarihi", baslangic)
      .lt("giris_tarihi", endExclusive)
      .order("giris_tarihi", { ascending: true });
    setGirisCikis(data || []);
  }

  function startEdit(row) {
    setEditing(row);
    setLockChecked(row.admin_locked !== false);
    const toLocal = (v) => {
      if (!v) return "";
      const d = new Date(v);
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      const hh = String(d.getHours()).padStart(2, "0");
      const min = String(d.getMinutes()).padStart(2, "0");
      return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
    };
    setEditValues({
      giris: toLocal(row.giris_tarihi),
      cikis: toLocal(row.cikis_tarihi),
    });
  }

  function startNew(grup) {
    setEditing({
      isNew: true,
      kullanici_id: calisan.kullanici_id ?? calisan.id,
      displayDate: grup.dateStr,
    });
    setLockChecked(false);
    setEditValues({ giris: `${grup.dateStr}T08:00`, cikis: `${grup.dateStr}T17:00` });
  }

  async function handleDeleteRow(row) {
    if (!window.confirm("Bu kaydı silmek istiyor musunuz?")) return;
    const { error } = await supabase
      .from("personel_giris_cikis_duzenli")
      .delete()
      .eq("id", row.id);
    if (!error) {
      await refreshGirisCikis();
    } else {
      alert(error.message || "Silme başarısız");
    }
  }

  function openHataBildir(grup, kayit) {
    setSelectedRecord({
      calisan,
      tarih: grup.dateStr,
      giris: kayit.isAbsent ? "-" : kayit.giris,
      cikis: kayit.isAbsent ? "Devamsız" : kayit.cikis,
    });
    setShowHataBildirimi(true);
  }

  async function handleSaveEdit() {
    const toIso = (s) => (s ? s.replace("T", " ") + ":00" : null);
    const toWorkday = (s) => {
      if (!s) return null;
      const d = new Date(s);
      const shifted = new Date(d.getTime() - dayStartHour * 60 * 60 * 1000);
      return format(shifted, "yyyy-MM-dd");
    };
    if (!editing) return;

    let error;
    if (editing.isNew) {
      const insertPayload = {
        kullanici_id: editing.kullanici_id,
        giris_tarihi: toIso(editValues.giris),
        cikis_tarihi: toIso(editValues.cikis),
        admin_locked: lockChecked,
        workday_date: toWorkday(editValues.giris) || null,
      };
      const resp = await supabase.from("personel_giris_cikis_duzenli").insert(insertPayload);
      error = resp.error;
    } else {
      const resp = await supabase
        .from("personel_giris_cikis_duzenli")
        .update({
          giris_tarihi: toIso(editValues.giris),
          cikis_tarihi: toIso(editValues.cikis),
          admin_locked: lockChecked,
          workday_date: toWorkday(editValues.giris) || null,
        })
        .eq("id", editing.id);
      error = resp.error;
    }

    if (!error) {
      setEditing(null);
      await refreshGirisCikis();
    } else {
      alert(error.message || "Güncelleme başarısız");
    }
  }

  async function handleClearWorkday() {
    if (!editing || editing.isNew) return;
    if (!window.confirm("Bu gün için workday_date kolonunu temizlemek istiyor musunuz?")) return;

    const { error } = await supabase
      .from("personel_giris_cikis_duzenli")
      .update({ workday_date: null })
      .eq("id", editing.id);

    if (!error) {
      setEditing(null);
      await refreshGirisCikis();
    } else {
      alert(error.message || "Temizleme başarısız");
    }
  }

  const editModalTitle = editing?.isNew
    ? "Yeni Kayıt Ekle"
    : "Saatleri Düzenle";

  const editModalSubtitle =
    editing?.displayDate
    || editing?.workday_date
    || (editValues.giris ? editValues.giris.split("T")[0] : null);

  function renderAdminActions(grup, kayit) {
    if (!session || !userProfile?.is_admin) return null;

    if (kayit._row) {
      return (
        <>
          <button type="button" className="btn-edit" onClick={() => startEdit(kayit._row)}>
            Düzenle
          </button>
          <button type="button" className="btn-delete" onClick={() => handleDeleteRow(kayit._row)}>
            Sil
          </button>
        </>
      );
    }

    return (
      <button type="button" className="btn-add" onClick={() => startNew(grup)}>
        Ekle
      </button>
    );
  }

  const canReportError = session && userProfile && !userProfile.is_admin;

  return (
    <div className="calisan-detay-root">
      {!inDialog && (
        <h2>
          {(calisan.isim || calisan.soyisim)
            ? `${calisan.isim || ""} ${calisan.soyisim || ""}`.trim()
            : `Kullanıcı ${calisan.kullanici_id ?? calisan.id}`} Çalışma Saatleri
        </h2>
      )}

      <div className="calisan-date-filters">
        <div className="calisan-date-field">
          <label>Başlangıç</label>
          <input
            type="date"
            value={baslangic}
            onChange={(e) => setBaslangic(e.target.value)}
          />
        </div>
        <div className="calisan-date-field">
          <label>Bitiş</label>
          <input
            type="date"
            value={bitis}
            onChange={(e) => setBitis(e.target.value)}
          />
        </div>
      </div>

      <div className="calisan-total-badge">
        Toplam Süre: {toplamSure} saat
      </div>

      {inDialog ? (
        <div className="calisan-gun-list">
          {gunGruplari.map((grup) => {
            const cardClass = grup.isAbsent
              ? "calisan-gun-card calisan-gun-card--absent"
              : grup.hasEdited
                ? "calisan-gun-card calisan-gun-card--edited"
                : grup.needsAttention
                  ? "calisan-gun-card calisan-gun-card--single"
                  : "calisan-gun-card";

            const badgeClass = grup.isAbsent
              ? "calisan-gun-card-badge calisan-gun-card-badge--absent"
              : grup.hasEdited
                ? "calisan-gun-card-badge calisan-gun-card-badge--edited"
                : "calisan-gun-card-badge calisan-gun-card-badge--ok";

            const badgeText = grup.isAbsent
              ? "Devamsız"
              : grup.hasEdited
                ? "Düzeltilmiş"
                : `${grup.gunToplam.toFixed(2)} saat`;

            return (
              <div key={grup.dateStr} className={cardClass}>
                <div className="calisan-gun-card-header">
                  <div>
                    <div className="calisan-gun-card-date">{grup.gunKisa}</div>
                    <div className="calisan-gun-card-weekday">{grup.gunAdi}</div>
                  </div>
                  <span className={badgeClass}>{badgeText}</span>
                </div>

                <div className="calisan-gun-card-body">
                  {grup.kayitlar.map((kayit, idx) => (
                    <div key={idx}>
                      <div className="calisan-gun-interval">
                        <div>
                          <div className="calisan-gun-interval-label">Giriş</div>
                          <div className="calisan-gun-interval-value">{kayit.giris}</div>
                        </div>
                        <div>
                          <div className="calisan-gun-interval-label">Çıkış</div>
                          <div className="calisan-gun-interval-value">{kayit.cikis}</div>
                        </div>
                        <div>
                          <div className="calisan-gun-interval-label">Süre</div>
                          <div className="calisan-gun-interval-value">{kayit.sure.toFixed(2)} sa</div>
                        </div>
                      </div>
                      <div className="calisan-gun-card-actions">
                        {canReportError && (
                          <button
                            type="button"
                            className="btn-hata"
                            onClick={() => openHataBildir(grup, kayit)}
                          >
                            Hata Bildir
                          </button>
                        )}
                        {renderAdminActions(grup, kayit)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
      <div className="mobile-scroll-wrap" style={{ 
        overflowX: "auto", 
        borderRadius: "8px", 
        border: "1px solid #e5e7eb",
        boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)"
      }}>
        <table className="mobile-table" style={{ 
          width: "100%", 
          borderCollapse: "collapse",
          backgroundColor: "white"
        }}>
        <thead>
          <tr style={{ backgroundColor: "#f9fafb" }}>
            <th style={{ 
              padding: "16px 12px", 
              textAlign: "left", 
              fontSize: "14px", 
              fontWeight: "600", 
              color: "#374151",
              borderBottom: "2px solid #e5e7eb"
            }}>Tarih</th>
            <th style={{ 
              padding: "16px 12px", 
              textAlign: "left", 
              fontSize: "14px", 
              fontWeight: "600", 
              color: "#374151",
              borderBottom: "2px solid #e5e7eb"
            }}>Gün</th>
            <th style={{ 
              padding: "16px 12px", 
              textAlign: "left", 
              fontSize: "14px", 
              fontWeight: "600", 
              color: "#374151",
              borderBottom: "2px solid #e5e7eb"
            }}>Giriş</th>
            <th style={{ 
              padding: "16px 12px", 
              textAlign: "left", 
              fontSize: "14px", 
              fontWeight: "600", 
              color: "#374151",
              borderBottom: "2px solid #e5e7eb"
            }}>Çıkış</th>
            <th style={{ 
              padding: "16px 12px", 
              textAlign: "left", 
              fontSize: "14px", 
              fontWeight: "600", 
              color: "#374151",
              borderBottom: "2px solid #e5e7eb"
            }}>Süre (saat)</th>
            {canReportError && (
            <th style={{ 
              padding: "16px 12px", 
              textAlign: "left", 
              fontSize: "14px", 
              fontWeight: "600", 
              color: "#374151",
              borderBottom: "2px solid #e5e7eb"
            }}>Hata Bildir</th>
            )}
            {session && userProfile && userProfile.is_admin && <th style={{ 
              padding: "16px 12px", 
              textAlign: "left", 
              fontSize: "14px", 
              fontWeight: "600", 
              color: "#374151",
              borderBottom: "2px solid #e5e7eb"
            }}>İşlem</th>}
          </tr>
        </thead>
        <tbody>
          {gunlerWithData.map((g, i) => {
            const row = g._row || null;
            const isManualEdit = row && row.admin_locked;
            const isAbsent = !row;
            return (
              <tr key={i} className={
                isAbsent ? "absent-row" : 
                isManualEdit ? "manual-edit-row" : ""
              } style={{
                borderBottom: "1px solid rgb(0, 0, 0)",
                transition: "background-color 0.2s",
                ...(isAbsent ? { backgroundColor: '#fef3c7' } : 
                    isManualEdit ? { backgroundColor: '#fee2e2' } : 
                    { backgroundColor: 'white' })
              }}>
                <td data-label="Tarih" style={{ 
                  padding: "16px 12px", 
                  fontSize: "14px", 
                  color: "#374151",
                  fontWeight: isAbsent ? "600" : "normal"
                }}>{g.tarih}</td>
                <td data-label="Gün" style={{ 
                  padding: "16px 12px", 
                  fontSize: "14px", 
                  color: "#374151",
                  fontWeight: isAbsent ? "600" : "normal"
                }}>{g.gun}</td>
                <td data-label="Giriş" style={{ 
                  padding: "16px 12px", 
                  fontSize: "14px", 
                  color: isAbsent ? "#ef4444" : "#374151",
                  fontWeight: isAbsent ? "600" : "normal"
                }}>{row ? g.giris : '-'}</td>
                <td data-label="Çıkış" style={{ 
                  padding: "16px 12px", 
                  fontSize: "14px", 
                  color: isAbsent ? "#ef4444" : "#374151",
                  fontWeight: isAbsent ? "600" : "normal"
                }}>{row ? g.cikis : 'Devamsız'}</td>
                <td data-label="Süre (saat)" style={{ 
                  padding: "16px 12px", 
                  fontSize: "14px", 
                  color: "#374151",
                  fontWeight: "600"
                }}>{g.sure.toFixed(2)}</td>
                {canReportError && (
                <td data-label="Hata Bildir" style={{ padding: "16px 12px" }}>
                  <button
                    type="button"
                    onClick={() => {
                      const grup = gunGruplari.find((gr) => gr.dateStr === g.tarih);
                      if (grup) openHataBildir(grup, { giris: g.giris, cikis: g.cikis, isAbsent: !row });
                    }}
                    style={{
                      padding: "8px 16px",
                      fontSize: "13px",
                      backgroundColor: "#dc2626",
                      color: "white",
                      border: "none",
                      borderRadius: "6px",
                      cursor: "pointer",
                      fontWeight: "500",
                      transition: "all 0.2s",
                      boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)"
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = "#b91c1c";
                      e.target.style.transform = "translateY(-1px)";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = "#dc2626";
                      e.target.style.transform = "translateY(0)";
                    }}
                  >
                    Hata Bildir
                  </button>
                </td>
                )}
                {session && userProfile && userProfile.is_admin && (
                  <td data-label="İşlem" style={{ padding: "16px 12px" }}>
                    {row ? (
                      <div className="responsive-flex" style={{ display: "flex", gap: "8px" }}>
                        <button type="button" onClick={() => startEdit(row)} style={{
                            padding: "8px 16px",
                            fontSize: "13px",
                            backgroundColor: "#3b82f6",
                            color: "white",
                            border: "none",
                            borderRadius: "6px",
                            cursor: "pointer",
                            fontWeight: "500",
                            transition: "all 0.2s",
                            boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)"
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = "#2563eb";
                            e.target.style.transform = "translateY(-1px)";
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = "#3b82f6";
                            e.target.style.transform = "translateY(0)";
                          }}
                        >
                          Düzenle
                        </button>
                        <button type="button" onClick={() => handleDeleteRow(row)} style={{
                            padding: "8px 16px",
                            fontSize: "13px",
                            backgroundColor: "#ef4444",
                            color: "white",
                            border: "none",
                            borderRadius: "6px",
                            cursor: "pointer",
                            fontWeight: "500",
                            transition: "all 0.2s",
                            boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)"
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = "#dc2626";
                            e.target.style.transform = "translateY(-1px)";
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = "#ef4444";
                            e.target.style.transform = "translateY(0)";
                          }}
                        >
                          Sil
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          const grup = gunGruplari.find((gr) => gr.dateStr === g.tarih);
                          if (grup) startNew(grup);
                        }}
                        style={{
                          padding: "8px 16px",
                          fontSize: "13px",
                          backgroundColor: "#10b981",
                          color: "white",
                          border: "none",
                          borderRadius: "6px",
                          cursor: "pointer",
                          fontWeight: "500",
                          transition: "all 0.2s",
                          boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)"
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = "#059669";
                          e.target.style.transform = "translateY(-1px)";
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = "#10b981";
                          e.target.style.transform = "translateY(0)";
                        }}
                      >
                        Ekle
                      </button>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr style={{ backgroundColor: "#f9fafb", borderTop: "2px solid #e5e7eb" }}>
            <td colSpan="4" style={{ 
              textAlign: "right", 
              padding: "16px 12px",
              fontSize: "14px",
              fontWeight: "600",
              color: "#374151"
            }}>Toplam Süre:</td>
            <td style={{ 
              padding: "16px 12px",
              fontSize: "14px",
              fontWeight: "600",
              color: "#374151"
            }}>{toplamSure} saat</td>
            {canReportError && <td></td>}
            {userProfile?.is_admin && <td></td>}
          </tr>
        </tfoot>
      </table>
      </div>
      )}

      <Modal
        open={Boolean(editing)}
        onClose={() => setEditing(null)}
        title={editModalTitle}
        zIndex={1100}
      >
        <div className="saat-edit-form">
          {editModalSubtitle && (
            <div className="saat-edit-subtitle">
              İş günü: <strong>{editModalSubtitle}</strong>
            </div>
          )}

          <div className="saat-edit-grid">
            <div className="saat-edit-field">
              <label htmlFor="edit-giris">Giriş</label>
              <input
                id="edit-giris"
                type="datetime-local"
                value={editValues.giris}
                onChange={(e) => setEditValues((p) => ({ ...p, giris: e.target.value }))}
              />
            </div>
            <div className="saat-edit-field">
              <label htmlFor="edit-cikis">Çıkış</label>
              <input
                id="edit-cikis"
                type="datetime-local"
                value={editValues.cikis}
                onChange={(e) => setEditValues((p) => ({ ...p, cikis: e.target.value }))}
              />
            </div>
          </div>

          <label className="saat-edit-toggle">
            <input
              type="checkbox"
              checked={lockChecked}
              onChange={(e) => setLockChecked(e.target.checked)}
            />
            <span className="saat-edit-toggle-box" aria-hidden="true" />
            <span className="saat-edit-toggle-text">
              <strong>Günü kilitle</strong>
              <small>Cihaz verisi bu kaydın üzerine yazmasın</small>
            </span>
          </label>

          <div className="saat-edit-actions">
            <button type="button" className="btn-primary" onClick={handleSaveEdit}>
              Kaydet
            </button>
            <button type="button" className="btn-secondary" onClick={() => setEditing(null)}>
              İptal
            </button>
            {!editing?.isNew && (
              <button type="button" className="btn-ghost" onClick={handleClearWorkday}>
                Gün anahtarını temizle
              </button>
            )}
          </div>
        </div>
      </Modal>

      <HataBildirimi
        open={showHataBildirimi}
        onClose={() => setShowHataBildirimi(false)}
        calisan={selectedRecord?.calisan}
        tarih={selectedRecord?.tarih}
        giris={selectedRecord?.giris}
        cikis={selectedRecord?.cikis}
      />
    </div>
  );
}

export default CalisanDetay;
